import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { type ApiError, fieldErrors } from '../../api/errors';
import { EntitySelect } from '../../components/EntityField';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from '../../components/WritingIntoAFiledMonth';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumOptions } from '../../lib/enums';
import { formatMoney, toMajorUnits, toMinorUnits } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { type CoveredDelivery, DeliveryPicker } from './DeliveryPicker';

const CHARGES_PATH = '/admin/v1/suppliers/freight-charges';

/** The list row this form edits — the GET-only shape, which carries what a charge covers. */
export interface ShippingCharge {
  id: string;
  carrierId: string;
  carrierName?: string | null;
  reference?: string | null;
  amountMinor: number;
  currency: string;
  fxRateMicro: number;
  chargedAt: string;
  allocationMethod: string;
  notes?: string | null;
  status: string;
  receiptIds?: string[];
  coveredReceipts?: CoveredDelivery[];
}

interface FormValues {
  carrierId: string;
  reference: string;
  amount: number | '';
  currency: string;
  fxRate: number | '';
  chargedAt: string;
  allocationMethod: string;
  notes: string;
  covered: CoveredDelivery[];
}

/** FX is stored against a 1e6 scale so a rate like 26.712345 survives the round trip exactly. */
const FX_SCALE = 1_000_000;

/** What a charge covers, in the shape the picker edits, from either field of the list row. */
function coverageOf(charge: ShippingCharge | null): CoveredDelivery[] {
  if (!charge) return [];
  return (
    charge.coveredReceipts ?? (charge.receiptIds ?? []).map((receiptId) => ({ receiptId, shareMinor: null }))
  );
}

function initialValues(charge: ShippingCharge | null, today: string): FormValues {
  if (!charge) {
    return {
      carrierId: '',
      reference: '',
      amount: '',
      currency: 'HNL',
      fxRate: '',
      // The day the carrier billed for the crossing, on the BAR's calendar. A UTC-sliced date
      // answers tomorrow from six in the evening, which is how a July crossing gets recorded
      // against August — the sixth finding of that exact shape in this repository.
      chargedAt: today,
      allocationMethod: 'byValue',
      notes: '',
      covered: [],
    };
  }
  return {
    carrierId: charge.carrierId,
    reference: charge.reference ?? '',
    amount: toMajorUnits(charge.amountMinor),
    currency: charge.currency,
    fxRate: charge.fxRateMicro / FX_SCALE,
    chargedAt: charge.chargedAt.slice(0, 10),
    allocationMethod: charge.allocationMethod,
    notes: charge.notes ?? '',
    covered: coverageOf(charge),
  };
}

/**
 * Recording what a carrier charged, and — the part a generic form cannot express — which
 * deliveries that charge covered.
 *
 * The two are one action, not two: the person holding the ferry's waybill knows both, and a charge
 * covering nothing prices nothing. Having them on separate screens is what left this tab able to
 * record a bill it could not attribute, while its own description promised the cost would be
 * spread across the deliveries it covered.
 *
 * Mounted only while it is open, and keyed on the charge, so it always opens on what is stored
 * rather than on whatever the previous edit left in the boxes.
 */
export function ShippingChargeForm({
  charge,
  locationId,
  alreadyCovered,
  onClose,
  onSaved,
}: {
  /** null when recording a new charge. */
  charge: ShippingCharge | null;
  locationId?: string;
  alreadyCovered?: Map<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = useIslandToday();
  const form = useForm<FormValues>({
    initialValues: initialValues(charge, today),
    validate: {
      carrierId: (v) => (v ? null : 'Say who brought the goods over'),
      amount: (v) => (v === '' || v < 0 ? 'Say what they charged' : null),
      fxRate: (v) => (v === '' || v <= 0 ? 'An exchange rate is required' : null),
      chargedAt: (v) => (v ? null : 'Say when they charged you'),
    },
  });

  const values = form.getValues();
  const byHand = values.allocationMethod === 'manual';
  const amountMinor = values.amount === '' ? 0 : (toMinorUnits(values.amount) ?? 0);
  const stated = values.covered.reduce((sum, c) => sum + (c.shareMinor ?? 0), 0);
  const allStated = values.covered.length > 0 && values.covered.every((c) => c.shareMinor !== null);

  /**
   * The same rule the API holds a hand-split to, checked while the boxes are still on screen: more
   * than the bill would push a covered delivery to a negative cost, and a fully itemised split
   * that lands short would be quietly topped up by value behind the operator's back.
   */
  const handSplitProblem =
    !byHand || values.covered.length === 0
      ? null
      : stated > amountMinor
        ? `That is ${formatMoney(stated - amountMinor, values.currency)} more than the bill.`
        : allStated && stated !== amountMinor
          ? `That leaves ${formatMoney(amountMinor - stated, values.currency)} of the bill unaccounted for — clear one of the amounts to have it shared out.`
          : null;

  /**
   * A crossing is the piece of paper most likely to arrive after the return has gone in: the ferry
   * bills days after it sails. The server refuses it once naming the month, this takes the reason,
   * and the same request goes again — see `WritingIntoAFiledMonth`.
   */
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const save = useMutation({
    mutationFn: async (v: FormValues & { filedMonthReason?: string }) => {
      const fields = {
        carrierId: v.carrierId,
        reference: v.reference || null,
        amountMinor: toMinorUnits(v.amount) ?? 0,
        currency: v.currency,
        fxRateMicro: Math.round(Number(v.fxRate) * FX_SCALE),
        chargedAt: v.chargedAt,
        allocationMethod: v.allocationMethod,
        notes: v.notes || null,
        // Attached only once the server has asked for it — see the dialog for why never before.
        ...(v.filedMonthReason ? { [FILED_MONTH_REASON_FIELD]: v.filedMonthReason } : {}),
      };
      const saved = charge
        ? await rawApi<{ charge: { id: string } }>('PATCH', `${CHARGES_PATH}/${charge.id}`, fields)
        : await rawApi<{ charge: { id: string } }>('POST', CHARGES_PATH, {
            ...fields,
            locationId,
            receiptIds: v.covered.map((c) => c.receiptId),
          });

      // Coverage travels on its own request because creating accepts only the list of deliveries,
      // not the amounts stated against them — and on an edit it is the endpoint that exists for
      // changing what a charge covered. It goes after the fields so the repricing it triggers uses
      // the corrected amount rather than the one being replaced.
      const changed = JSON.stringify(v.covered) !== JSON.stringify(coverageOf(charge));
      const needsShares = v.covered.some((c) => c.shareMinor !== null);
      if (changed && (charge || needsShares)) {
        await rawApi('PUT', `${CHARGES_PATH}/${saved.charge.id}/receipts`, { receipts: v.covered });
      }
      return v.covered.length;
    },
    onSuccess: (count) => {
      setFiledMonthWarning(null);
      onSaved();
      onClose();
      notifyDone(
        charge ? 'Shipping charge updated' : 'Shipping charge recorded',
        count === 0
          ? 'It is not spread over anything yet — say which deliveries it covered and the cost will follow the goods.'
          : `Spread across ${count} ${count === 1 ? 'delivery' : 'deliveries'}.`,
      );
    },
    onError: (err: Error) => {
      if (isFiledMonthWarning(err)) return setFiledMonthWarning(err);
      // Field-level problems belong on the fields; only the summary goes to the toast.
      const byField = fieldErrors(err);
      if (Object.keys(byField).length) form.setErrors(byField);
      notifyError(err, 'save this shipping charge');
    },
  });

  return (
    <Modal
      opened
      onClose={onClose}
      title={charge ? 'Edit this shipping charge' : 'Record a shipping charge'}
      size="xl"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <form
        onSubmit={form.onSubmit((v) => {
          if (handSplitProblem) return;
          save.mutate(v);
        })}
      >
        <Stack gap="md">
          <EntitySelect
            entity="carrier"
            label="Who brought it over"
            description="The ferry or freight company that billed you — not the supplier who sold you the goods"
            required
            value={values.carrierId || null}
            onChange={(v) => form.setFieldValue('carrierId', v ?? '')}
            error={form.errors.carrierId}
            disabled={save.isPending}
          />

          <Group grow align="flex-start">
            <TextInput
              label="Their reference"
              description="Waybill or bill-of-lading number, so this can be matched to their invoice"
              {...form.getInputProps('reference')}
              disabled={save.isPending}
            />
            <DateInput
              label="Date they charged you"
              required
              value={values.chargedAt || null}
              onChange={(v) => form.setFieldValue('chargedAt', v ?? '')}
              valueFormat="D MMM YYYY"
              error={form.errors.chargedAt}
              popoverProps={{ withinPortal: true }}
              disabled={save.isPending}
            />
          </Group>

          <Group grow align="flex-start">
            <NumberInput
              label="What they charged"
              required
              {...form.getInputProps('amount')}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator=","
              hideControls
              disabled={save.isPending}
            />
            <Select
              label="Currency"
              description="They may bill in lempiras even when the goods were invoiced in dollars"
              required
              data={enumOptions('currency')}
              {...form.getInputProps('currency')}
              comboboxProps={{ withinPortal: true }}
              allowDeselect={false}
              disabled={save.isPending}
            />
            <NumberInput
              label="Exchange rate used"
              description="Lempiras per dollar on the day they billed you"
              required
              {...form.getInputProps('fxRate')}
              decimalScale={6}
              disabled={save.isPending}
            />
          </Group>

          <Divider
            label="What this charge covered"
            labelPosition="left"
            styles={{ label: { fontWeight: 600 } }}
          />

          <Select
            label="How to spread the cost"
            description="By value suits mixed loads; by weight suits heavy goods; by hand when they itemised it"
            required
            data={enumOptions('landedCostAllocation')}
            {...form.getInputProps('allocationMethod')}
            comboboxProps={{ withinPortal: true }}
            allowDeselect={false}
            disabled={save.isPending}
          />

          <DeliveryPicker
            value={values.covered}
            onChange={(next) => form.setFieldValue('covered', next)}
            currency={values.currency}
            statedShares={byHand}
            alreadyCovered={alreadyCovered}
            disabled={save.isPending}
          />

          {byHand && values.covered.length > 0 && (
            <Text size="sm" c={handSplitProblem ? 'red.7' : 'dimmed'}>
              {formatMoney(stated, values.currency)} of {formatMoney(amountMinor, values.currency)} written in
              by hand. {handSplitProblem ?? ''}
            </Text>
          )}

          {values.covered.length === 0 && (
            <Alert color="yellow" variant="light" title="This charge is not spread over anything">
              You can save it now and come back, but until you say which deliveries it covered it will not
              change what any of your stock cost.
            </Alert>
          )}

          <TextInput label="Note" {...form.getInputProps('notes')} disabled={save.isPending} />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending} disabled={Boolean(handSplitProblem)}>
              {charge ? 'Save changes' : 'Record charge'}
            </Button>
          </Group>
        </Stack>
      </form>
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={save.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(filedMonthReason) => save.mutate({ ...values, filedMonthReason })}
      />
    </Modal>
  );
}
